/**
 * STORAGE VERSIONADO v2.2
 * Sistema robusto de persistencia con soporte para comidas componibles
 * ✅ Función remove() corregida con guardado explícito
 * ✅ v2.2: Limpieza automática de días viejos del mealPlan (últimos 90 días)
 */
import { validateStockItem, validateShoppingItem } from './validators';
import { MEAL_STATUS, MEAL_SOURCE_TYPE, normalizeCategory } from './types';

// ============================================
// CONFIGURACIÓN
// ============================================
const STORAGE_VERSION = 2;
const STORAGE_PREFIX = 'mealcycle_v';
const STORAGE_KEYS = {
  VERSION: `${STORAGE_PREFIX}version`,
  STOCK: `${STORAGE_PREFIX}${STORAGE_VERSION}_stock`,
  RECIPES: `${STORAGE_PREFIX}${STORAGE_VERSION}_recipes`,
  MEAL_PLAN: `${STORAGE_PREFIX}${STORAGE_VERSION}_meal_plan`,
  SHOPPING_LIST: `${STORAGE_PREFIX}${STORAGE_VERSION}_shopping_list`,
  SHOPPING_LISTS: `${STORAGE_PREFIX}${STORAGE_VERSION}_shopping_lists`,
  METADATA: `${STORAGE_PREFIX}${STORAGE_VERSION}_metadata`
};
const KEYS = STORAGE_KEYS;

// ============================================
// UTILIDADES BASE
// ============================================
const safeJsonParse = (str, defaultValue = null) => {
  try {
    return str ? JSON.parse(str) : defaultValue;
  } catch (error) {
    console.error('Error parsing JSON:', error);
    return defaultValue;
  }
};

const safeJsonStringify = (obj) => {
  try {
    return JSON.stringify(obj);
  } catch (error) {
    console.error('Error stringifying JSON:', error);
    return null;
  }
};

const generateId = () => {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// ============================================
// CAPA BASE DE STORAGE
// ============================================
class BaseStorage {
  get(key) {
    try {
      const item = localStorage.getItem(key);
      return safeJsonParse(item);
    } catch (error) {
      console.error(`Error getting ${key}:`, error);
      return null;
    }
  }

  set(key, value) {
    try {
      const serialized = safeJsonStringify(value);
      if (serialized) {
        localStorage.setItem(key, serialized);
        this.updateMetadata(key);
        return true;
      }
      return false;
    } catch (error) {
      console.error(`Error setting ${key}:`, error);
      return false;
    }
  }

  remove(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Error removing ${key}:`, error);
      return false;
    }
  }

  updateMetadata(key) {
    const metadata = this.get(KEYS.METADATA) || {};
    metadata[key] = {
      lastModified: new Date().toISOString(),
      version: STORAGE_VERSION
    };
    localStorage.setItem(KEYS.METADATA, safeJsonStringify(metadata));
  }

  getMetadata() {
    return this.get(KEYS.METADATA) || {};
  }
}

const baseStorage = new BaseStorage();

// ============================================
// SISTEMA DE VERSIONADO Y MIGRACIONES
// ============================================
class StorageVersionManager {
  getCurrentVersion() {
    return baseStorage.get(KEYS.VERSION) || 0;
  }

  setVersion(version) {
    baseStorage.set(KEYS.VERSION, version);
  }

  needsMigration() {
    const currentVersion = this.getCurrentVersion();
    return currentVersion < STORAGE_VERSION;
  }

  migrate() {
    const currentVersion = this.getCurrentVersion();

    if (currentVersion === 0) {
      console.log('Primera instalación detectada');
      this.setVersion(STORAGE_VERSION);
      return true;
    }

    if (currentVersion < STORAGE_VERSION) {
      console.log(`Migrando de v${currentVersion} a v${STORAGE_VERSION}`);

      if (currentVersion === 1) {
        this.migrateV1ToV2();
      }

      this.setVersion(STORAGE_VERSION);
      return true;
    }

    return false;
  }

  migrateV1ToV2() {
    console.log('🔄 Migrando MealPlan a formato v2.0...');

    const oldKey = 'mealcycle_v1_meal_plan';
    const oldPlan = baseStorage.get(oldKey);

    if (!oldPlan || oldPlan.length === 0) {
      console.log('✓ No hay datos antiguos para migrar');
      return;
    }

    const newPlan = oldPlan.map(meal => {
      const converted = {
        date: meal.date,
        breakfast: meal.breakfast ? this.convertToMealData(meal.breakfast) : null,
        lunch: meal.lunch ? this.convertToMealData(meal.lunch) : null,
        snack: meal.snack ? this.convertToMealData(meal.snack) : null,
        dinner: meal.dinner ? this.convertToMealData(meal.dinner) : null,
        cooked: meal.cooked || {},
        cancelled: meal.cancelled || {}
      };
      return converted;
    });

    baseStorage.set(KEYS.MEAL_PLAN, newPlan);
    console.log(`✓ Migrados ${newPlan.length} días del plan`);
  }

  convertToMealData(recipeId) {
    return {
      type: MEAL_SOURCE_TYPE.RECIPE,
      recipeId: recipeId,
      recipeName: 'Receta (migrada)',
      components: [],
      servings: 1
    };
  }
}

export const versionManager = new StorageVersionManager();

// ============================================
// STOCK STORAGE
// ============================================
export const stockStorage = {
  getAll() {
    return baseStorage.get(KEYS.STOCK) || [];
  },

  save(stock) {
    return baseStorage.set(KEYS.STOCK, stock);
  },

  getById(id) {
    const stock = this.getAll();
    return stock.find(item => item.id === id) || null;
  },

  add(item) {
    const validation = validateStockItem(item);
    if (!validation.isValid) {
      throw new Error(`Validación fallida: ${validation.errors.join(', ')}`);
    }

    const stock = this.getAll();

    const newItem = {
      id: generateId(),
      name: item.name.trim(),
      category: item.category,
      quantity: parseFloat(item.quantity),
      unit: item.unit,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    stock.push(newItem);
    this.save(stock);

    console.log('✓ Item agregado al stock:', newItem.name);
    return newItem;
  },

  update(id, updates) {
    const stock = this.getAll();
    const index = stock.findIndex(item => item.id === id);

    if (index === -1) {
      throw new Error('Item no encontrado');
    }

    const updatedItem = {
      ...stock[index],
      ...updates,
      quantity: updates.quantity !== undefined ? parseFloat(updates.quantity) : stock[index].quantity,
      updatedAt: new Date().toISOString()
    };

    const validation = validateStockItem(updatedItem);
    if (!validation.isValid) {
      throw new Error(`Validación fallida: ${validation.errors.join(', ')}`);
    }

    stock[index] = updatedItem;
    this.save(stock);

    console.log('✓ Item actualizado:', updatedItem.name);
    return updatedItem;
  },

  delete(id) {
    const stock = this.getAll();
    const filtered = stock.filter(item => item.id !== id);

    if (filtered.length === stock.length) {
      throw new Error('Item no encontrado');
    }

    this.save(filtered);
    console.log('✓ Item eliminado del stock');
    return true;
  },

  deduct(name, quantity, unit) {
    const stock = this.getAll();
    const item = stock.find(s =>
      s.name.toLowerCase() === name.toLowerCase() &&
      s.unit === unit
    );

    if (!item) {
      throw new Error(`Ingrediente no encontrado: ${name}`);
    }

    if (item.quantity < quantity) {
      throw new Error(`Stock insuficiente de ${name}. Disponible: ${item.quantity} ${unit}, Necesario: ${quantity} ${unit}`);
    }

    const newQuantity = item.quantity - quantity;
    if (newQuantity <= 0) {
      this.delete(item.id);
      console.log(`✓ ${name} agotado y eliminado del stock`);
    } else {
      this.update(item.id, { quantity: newQuantity });
      console.log(`✓ Descontado ${quantity} ${unit} de ${name}. Quedan: ${newQuantity} ${unit}`);
    }

    return true;
  },

  findByNameAndUnit(name, unit) {
    const stock = this.getAll();
    return stock.find(s =>
      s.name.toLowerCase() === name.toLowerCase() &&
      s.unit === unit
    ) || null;
  },

  getLowStockItems() {
    const stock = this.getAll();
    return stock.filter(item => item.quantity < 0.1);
  }
};

// ============================================
// MEAL PLAN STORAGE v2.1 - CORREGIDO
// ============================================
export const mealPlanStorage = {
  getAll() {
    return baseStorage.get(KEYS.MEAL_PLAN) || [];
  },

  save(mealPlan) {
    console.log('💾 [STORAGE] Guardando mealPlan:', mealPlan.length, 'días');
    const result = baseStorage.set(KEYS.MEAL_PLAN, mealPlan);
    console.log('💾 [STORAGE] Resultado del guardado:', result);
    return result;
  },

  getByDate(date) {
    const mealPlan = this.getAll();
    return mealPlan.find(m => m.date === date) || null;
  },

  set(date, mealType, mealData) {
    const mealPlan = this.getAll();
    const existing = mealPlan.find(m => m.date === date);

    if (existing) {
      existing[mealType] = mealData;

      if (existing.cooked && existing.cooked[mealType]) {
        existing.cooked[mealType] = false;
      }
      if (existing.cancelled && existing.cancelled[mealType]) {
        existing.cancelled[mealType] = false;
      }
    } else {
      const newMeal = {
        date,
        breakfast: mealType === 'breakfast' ? mealData : null,
        lunch: mealType === 'lunch' ? mealData : null,
        snack: mealType === 'snack' ? mealData : null,
        dinner: mealType === 'dinner' ? mealData : null,
        cooked: {},
        cancelled: {}
      };
      mealPlan.push(newMeal);
    }

    this.save(mealPlan);
    console.log(`✓ Comida planificada para ${date} - ${mealType}`);
    return true;
  },

  markAsCooked(date, mealType) {
    const mealPlan = this.getAll();
    const meal = mealPlan.find(m => m.date === date);

    if (!meal) {
      throw new Error('Comida no encontrada');
    }

    if (!meal.cooked) meal.cooked = {};
    meal.cooked[mealType] = true;

    if (meal.cancelled && meal.cancelled[mealType]) {
      meal.cancelled[mealType] = false;
    }

    this.save(mealPlan);
    console.log(`✓ Comida marcada como cocinada: ${date} - ${mealType}`);
    return true;
  },

  unmarkAsCooked(date, mealType) {
    const mealPlan = this.getAll();
    const meal = mealPlan.find(m => m.date === date);

    if (!meal) {
      throw new Error('Comida no encontrada');
    }

    if (meal.cooked) {
      meal.cooked[mealType] = false;
    }

    this.save(mealPlan);
    console.log(`✓ Comida desmarcada como cocinada: ${date} - ${mealType}`);
    return true;
  },

  markAsCancelled(date, mealType) {
    const mealPlan = this.getAll();
    const meal = mealPlan.find(m => m.date === date);

    if (!meal) {
      throw new Error('Comida no encontrada');
    }

    if (!meal.cancelled) meal.cancelled = {};
    meal.cancelled[mealType] = true;

    if (meal.cooked && meal.cooked[mealType]) {
      meal.cooked[mealType] = false;
    }

    this.save(mealPlan);
    console.log(`✓ Comida cancelada: ${date} - ${mealType}`);
    return true;
  },

  remove(date, mealType) {
    console.log('🗑️ [STORAGE] Eliminando comida:', date, mealType);

    const mealPlan = this.getAll();
    console.log('📋 [STORAGE] Plan actual:', mealPlan.length, 'días');

    const index = mealPlan.findIndex(m => m.date === date);
    console.log('🔍 [STORAGE] Índice encontrado:', index);

    if (index === -1) {
      console.error('❌ [STORAGE] Día no encontrado');
      throw new Error('Comida no encontrada');
    }

    const currentMeal = mealPlan[index][mealType];
    console.log('🍽️ [STORAGE] Comida actual:', currentMeal);

    mealPlan[index][mealType] = null;
    console.log('✓ [STORAGE] Comida marcada como null');

    if (mealPlan[index].cooked) {
      mealPlan[index].cooked[mealType] = false;
    }
    if (mealPlan[index].cancelled) {
      mealPlan[index].cancelled[mealType] = false;
    }

    const allEmpty = ['breakfast', 'lunch', 'snack', 'dinner'].every(
      type => !mealPlan[index][type]
    );
    console.log('🔍 [STORAGE] ¿Día vacío?', allEmpty);

    if (allEmpty) {
      console.log('🗑️ [STORAGE] Eliminando día completo');
      mealPlan.splice(index, 1);
    }

    console.log('💾 [STORAGE] Guardando cambios...');
    const saveResult = this.save(mealPlan);
    console.log('💾 [STORAGE] Resultado del guardado:', saveResult);

    const verification = this.getAll();
    console.log('✓ [STORAGE] Verificación post-guardado:', verification.length, 'días');
    console.log(`✅ [STORAGE] Comida eliminada: ${date} - ${mealType}`);
    return true;
  },

  getMealStatus(date, mealType) {
    const meal = this.getByDate(date);

    if (!meal || !meal[mealType]) {
      return MEAL_STATUS.EMPTY;
    }
    if (meal.cooked && meal.cooked[mealType]) {
      return MEAL_STATUS.COOKED;
    }
    if (meal.cancelled && meal.cancelled[mealType]) {
      return MEAL_STATUS.CANCELLED;
    }
    return MEAL_STATUS.PLANNED;
  }
};

// ============================================
// RECIPES STORAGE
// ============================================
export const recipeStorage = {
  getAll() {
    return baseStorage.get(KEYS.RECIPES) || [];
  },

  save(recipes) {
    return baseStorage.set(KEYS.RECIPES, recipes);
  },

  getById(id) {
    const recipes = this.getAll();
    return recipes.find(r => r.id === id) || null;
  }
};

// ============================================
// SHOPPING LISTS STORAGE
// ============================================
export const shoppingListsStorage = {
  getAll: () => baseStorage.get(KEYS.SHOPPING_LISTS) || [],

  save: (lists) => baseStorage.set(KEYS.SHOPPING_LISTS, lists),

  getById: (listId) => {
    const lists = shoppingListsStorage.getAll();
    return lists.find(l => l.id === listId) || null;
  },

  create: (listData) => {
    const lists = shoppingListsStorage.getAll();
    const newList = {
      id: generateId(),
      name: listData.name || 'Lista sin nombre',
      startDate: listData.startDate || null,
      endDate: listData.endDate || null,
      items: listData.items || [],
      createdAt: new Date().toISOString(),
      isAutoGenerated: listData.isAutoGenerated || false
    };
    lists.push(newList);
    shoppingListsStorage.save(lists);
    console.log('✓ Lista creada:', newList.name);
    return newList;
  },

  update: (listId, updates) => {
    const lists = shoppingListsStorage.getAll();
    const index = lists.findIndex(l => l.id === listId);
    if (index !== -1) {
      lists[index] = {
        ...lists[index],
        ...updates,
        updatedAt: new Date().toISOString()
      };
      shoppingListsStorage.save(lists);
      console.log('✓ Lista actualizada');
      return lists[index];
    }
    throw new Error('Lista no encontrada');
  },

  delete: (listId) => {
    const lists = shoppingListsStorage.getAll();
    const filtered = lists.filter(l => l.id !== listId);
    if (filtered.length === lists.length) {
      throw new Error('Lista no encontrada');
    }
    shoppingListsStorage.save(filtered);
    console.log('✓ Lista eliminada');
    return true;
  },

  addItem: (listId, item) => {
    const list = shoppingListsStorage.getById(listId);
    if (!list) throw new Error('Lista no encontrada');

    const newItem = {
      id: generateId(),
      name: item.name,
      quantity: parseFloat(item.quantity) || 1,
      unit: item.unit || 'unidad',
      category: item.category || 'otros',
      price: item.price ? parseFloat(item.price) : null,
      purchased: false,
      adjustedQuantity: null,
      isManual: item.isManual || false
    };

    list.items.push(newItem);
    shoppingListsStorage.update(listId, { items: list.items });
    console.log('✓ Item agregado a la lista');
    return newItem;
  },

  updateItem: (listId, itemId, updates) => {
    const list = shoppingListsStorage.getById(listId);
    if (!list) throw new Error('Lista no encontrada');

    const itemIndex = list.items.findIndex(i => i.id === itemId);
    if (itemIndex === -1) throw new Error('Item no encontrado');

    list.items[itemIndex] = {
      ...list.items[itemIndex],
      ...updates
    };

    shoppingListsStorage.update(listId, { items: list.items });
    return list.items[itemIndex];
  },

  deleteItem: (listId, itemId) => {
    const list = shoppingListsStorage.getById(listId);
    if (!list) throw new Error('Lista no encontrada');

    list.items = list.items.filter(i => i.id !== itemId);
    shoppingListsStorage.update(listId, { items: list.items });
    console.log('✓ Item eliminado de la lista');
    return true;
  },

  togglePurchased: (listId, itemId) => {
    const list = shoppingListsStorage.getById(listId);
    if (!list) throw new Error('Lista no encontrada');

    const item = list.items.find(i => i.id === itemId);
    if (!item) throw new Error('Item no encontrado');

    item.purchased = !item.purchased;
    shoppingListsStorage.update(listId, { items: list.items });
    console.log(`✓ Item ${item.purchased ? 'marcado' : 'desmarcado'} como comprado`);
    return true;
  },

  setAdjustedQuantity: (listId, itemId, quantity) => {
    const list = shoppingListsStorage.getById(listId);
    if (!list) throw new Error('Lista no encontrada');

    const item = list.items.find(i => i.id === itemId);
    if (!item) throw new Error('Item no encontrado');

    item.adjustedQuantity = parseFloat(quantity);
    shoppingListsStorage.update(listId, { items: list.items });
    console.log(`✓ Cantidad ajustada para ${item.name}`);
    return true;
  },

  addPurchasedToStock: (listId) => {
    const list = shoppingListsStorage.getById(listId);
    if (!list) throw new Error('Lista no encontrada');

    const purchasedItems = list.items.filter(item => item.purchased);

    purchasedItems.forEach(item => {
      const quantityToAdd = item.adjustedQuantity || item.quantity;
      const migratedCategory = normalizeCategory(item.category);
      const existingStock = stockStorage.findByNameAndUnit(item.name, item.unit);

      if (existingStock) {
        stockStorage.update(existingStock.id, {
          quantity: existingStock.quantity + quantityToAdd,
          price: item.price || existingStock.price,
          category: migratedCategory
        });
      } else {
        stockStorage.add({
          name: item.name,
          category: migratedCategory,
          quantity: quantityToAdd,
          unit: item.unit,
          price: item.price
        });
      }
    });

    list.items = list.items.filter(item => !item.purchased);
    shoppingListsStorage.update(listId, { items: list.items });

    console.log(`✓ ${purchasedItems.length} items agregados al stock`);
    return purchasedItems.length;
  },

  generateFromPlan: (startDate, endDate, listName) => {
    console.warn('⚠️ generateFromPlan necesita actualización para componentes');
    return shoppingListsStorage.create({
      name: listName || `Lista ${startDate} al ${endDate}`,
      startDate,
      endDate,
      items: [],
      isAutoGenerated: true
    });
  },

  getTotalPrice: (listId) => {
    const list = shoppingListsStorage.getById(listId);
    if (!list) return 0;

    return list.items.reduce((total, item) => {
      if (item.price) {
        const qty = item.adjustedQuantity || item.quantity;
        return total + (item.price * qty);
      }
      return total;
    }, 0);
  }
};

// Compatibilidad con código antiguo (singular)
export const shoppingListStorage = {
  get: () => {
    const lists = shoppingListsStorage.getAll();
    if (lists.length > 0) {
      return lists[0].items || [];
    }
    return [];
  },

  save: (items) => {
    const lists = shoppingListsStorage.getAll();
    if (lists.length > 0) {
      shoppingListsStorage.update(lists[0].id, { items });
    } else {
      shoppingListsStorage.create({
        name: 'Lista principal',
        items
      });
    }
  },

  clear: () => {
    const lists = shoppingListsStorage.getAll();
    if (lists.length > 0) {
      shoppingListsStorage.update(lists[0].id, { items: [] });
    }
  },

  togglePurchased: (itemId) => {
    const lists = shoppingListsStorage.getAll();
    if (lists.length > 0) {
      shoppingListsStorage.togglePurchased(lists[0].id, itemId);
    }
  },

  setAdjustedQuantity: (itemId, quantity) => {
    const lists = shoppingListsStorage.getAll();
    if (lists.length > 0) {
      shoppingListsStorage.setAdjustedQuantity(lists[0].id, itemId, quantity);
    }
  }
};

// ============================================
// ✅ v2.2 — LIMPIEZA DE DÍAS VIEJOS
// Mantiene los últimos DAYS_TO_KEEP días del mealPlan.
// El historial más viejo no tiene valor operativo y hace crecer
// el array indefinidamente en localStorage y Firestore.
// Se ejecuta una vez por sesión desde initializeStorage().
// ============================================

const DAYS_TO_KEEP = 90;

const cleanOldMealPlanDays = () => {
  try {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - DAYS_TO_KEEP);

    // Formato YYYY-MM-DD — mismo formato que usa el resto del sistema
    const cutoffStr = cutoff.toISOString().split('T')[0];

    const plan = baseStorage.get(KEYS.MEAL_PLAN) || [];
    const filtered = plan.filter(m => m.date >= cutoffStr);

    if (filtered.length < plan.length) {
      const removed = plan.length - filtered.length;
      baseStorage.set(KEYS.MEAL_PLAN, filtered);
      console.log(`🧹 MealPlan: ${removed} día(s) con más de ${DAYS_TO_KEEP} días eliminados. Quedan ${filtered.length}.`);
    }
  } catch (error) {
    // No bloquear la app si falla la limpieza
    console.warn('⚠️ Error en limpieza de mealPlan:', error);
  }
};

// ============================================
// INICIALIZACIÓN
// ============================================
export const initializeStorage = () => {
  console.log('🔧 Inicializando sistema de storage v2.2...');

  if (versionManager.needsMigration()) {
    console.log('⚡ Ejecutando migraciones...');
    versionManager.migrate();
  }

  // ✅ v2.2: limpiar días viejos del mealPlan una vez por sesión
  cleanOldMealPlanDays();

  console.log('✓ Storage inicializado correctamente');
  console.log(`📦 Versión actual: ${STORAGE_VERSION}`);

  return true;
};